(function () {
  function clearPendingState() {
    if (document && document.documentElement) {
      document.documentElement.classList.remove("b2b-images-pending");
    }
  }

  var root = window.B2BImagePersonalization || {};
  if (root.enabled === false) {
    clearPendingState();
    return;
  }

  function sanitizeCompanyCode(value) {
    if (!value) return "";
    return String(value).trim().replace(/[^A-Za-z0-9_-]/g, "");
  }

  var companyCode = sanitizeCompanyCode(root.companyCode);
  if (!companyCode) {
    clearPendingState();
    return;
  }

  var processed = new WeakSet();
  var availabilityCache = new Map();
  var PERSONALIZE_SELECTOR = '[data-b2b-personalize="true"] img';

  function splitSrcsetEntry(entry) {
    var trimmed = entry.trim();
    if (!trimmed) return null;

    var descriptorMatch = trimmed.match(/\s+([0-9]+[wx])$/);
    if (!descriptorMatch) return { url: trimmed, descriptor: "" };

    var descriptor = descriptorMatch[1];
    var url = trimmed.slice(0, trimmed.length - descriptorMatch[0].length);
    return { url: url, descriptor: descriptor };
  }

  function buildPersonalizedUrl(rawUrl, code, baseOverride) {
    if (!rawUrl) return "";

    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.origin);
    } catch (err) {
      return "";
    }

    var pathname = parsed.pathname || "";
    var slashIndex = pathname.lastIndexOf("/");
    var dir = slashIndex >= 0 ? pathname.slice(0, slashIndex + 1) : "";
    var file = slashIndex >= 0 ? pathname.slice(slashIndex + 1) : pathname;

    var match = file.match(/^(.*?)(_(?:\d+x\d*|x\d+))?(\.[A-Za-z0-9]+)$/);
    if (!match) return "";

    var baseName = baseOverride || match[1];
    var sizeSuffix = match[2] || "";
    var extension = match[3];

    if (baseName.endsWith("_" + code)) {
      return parsed.toString();
    }

    parsed.pathname = dir + baseName + "_" + code + sizeSuffix + extension;
    return parsed.toString();
  }

  function stripTrailingUuid(baseName) {
    if (!baseName) return "";
    return String(baseName).replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "");
  }

  function isRandomTailToken(token) {
    if (!token) return false;
    return (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token) ||
      /^[0-9a-f]{10,}$/i.test(token) ||
      /^[0-9]{8,}$/i.test(token)
    );
  }

  function stripShotNoiseByConvention(baseName) {
    if (!baseName) return "";
    var parts = String(baseName).split("_");

    // Only apply this fallback for B2B naming scheme:
    // B2B_SKU_Product_Shot (product part may include extra underscores).
    if (parts.length < 4 || parts[0] !== "B2B") return baseName;

    var last = parts[parts.length - 1];
    if (!isRandomTailToken(last)) return baseName;

    return parts.slice(0, -1).join("_");
  }

  function buildPersonalizedUrlCandidates(rawUrl, code) {
    if (!rawUrl) return [];

    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.origin);
    } catch (err) {
      return [];
    }

    var pathname = parsed.pathname || "";
    var slashIndex = pathname.lastIndexOf("/");
    var file = slashIndex >= 0 ? pathname.slice(slashIndex + 1) : pathname;
    var match = file.match(/^(.*?)(_(?:\d+x\d*|x\d+))?(\.[A-Za-z0-9]+)$/);
    if (!match) {
      var single = buildPersonalizedUrl(rawUrl, code);
      return single ? [{ url: single, base: "" }] : [];
    }

    var baseName = match[1];
    var baseCandidates = [baseName];
    var uuidStripped = stripTrailingUuid(baseName);
    if (uuidStripped && uuidStripped !== baseName) baseCandidates.push(uuidStripped);
    var conventionStripped = stripShotNoiseByConvention(baseName);
    if (conventionStripped && baseCandidates.indexOf(conventionStripped) === -1) {
      baseCandidates.push(conventionStripped);
    }

    var urls = [];
    baseCandidates.forEach(function (candidateBase) {
      var built = buildPersonalizedUrl(rawUrl, code, candidateBase);
      if (!built) return;
      if (urls.some(function (entry) { return entry.url === built; })) return;
      urls.push({ url: built, base: candidateBase });
    });

    return urls;
  }

  function rewriteSrcset(srcset, code, baseOverride) {
    if (!srcset) return "";
    return srcset
      .split(",")
      .map(function (entry) {
        var parsed = splitSrcsetEntry(entry);
        if (!parsed || !parsed.url) return entry;

        var altUrl = buildPersonalizedUrl(parsed.url, code, baseOverride);
        if (!altUrl) return entry.trim();

        return parsed.descriptor ? altUrl + " " + parsed.descriptor : altUrl;
      })
      .join(", ");
  }

  function checkImageExists(url) {
    if (!url) return Promise.resolve(false);
    if (availabilityCache.has(url)) return Promise.resolve(availabilityCache.get(url));

    return new Promise(function (resolve) {
      var probe = new Image();
      probe.onload = function () {
        availabilityCache.set(url, true);
        resolve(true);
      };
      probe.onerror = function () {
        availabilityCache.set(url, false);
        resolve(false);
      };
      probe.src = url.split('?')[0] + '?v=' + Date.now();
    });
  }

  function swapImage(img) {
    if (!img || processed.has(img)) return;
    processed.add(img);

    var originalSrc = img.getAttribute("src") || img.currentSrc || "";
    if (!originalSrc) return;

    var candidates = buildPersonalizedUrlCandidates(originalSrc, companyCode);
    if (!candidates.length) return;

    var originalSrcset = img.getAttribute("srcset") || "";
    var found = null;
    var sequence = Promise.resolve(false);
    candidates.forEach(function (candidate) {
      sequence = sequence.then(function (alreadyFound) {
        if (alreadyFound) return true;
        return checkImageExists(candidate.url).then(function (exists) {
          if (!exists) return false;
          found = candidate;
          return true;
        });
      });
    });

    sequence.then(function (exists) {
      if (!exists || !found) return;
      var personalizedSrc = found.url;
      var personalizedSrcset = rewriteSrcset(originalSrcset, companyCode, found.base);

      img.setAttribute("data-b2b-original-src", originalSrc);
      img.setAttribute("data-b2b-personalized-src", personalizedSrc);

      if (originalSrcset) {
        img.setAttribute("data-b2b-original-srcset", originalSrcset);
        if (personalizedSrcset) {
          img.setAttribute("srcset", personalizedSrcset);
        }
      }

      img.setAttribute("src", personalizedSrc);
    });
  }

  function scan(container) {
    var scope = container || document;
    var images = scope.querySelectorAll(PERSONALIZE_SELECTOR);
    images.forEach(swapImage);
  }

  function observeDom() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return;

          if (node.matches && node.matches(PERSONALIZE_SELECTOR)) {
            swapImage(node);
            return;
          }

          if (node.querySelectorAll) {
            scan(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    scan(document);
    observeDom();
    clearPendingState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
